import { useState, type ReactNode } from 'react';
import type { SelfServeSettings } from '@traceio-org/platform-contracts';
import { PageSection } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { RadioGroup, ReasonField, reasonError } from '../../components/fields';
import { DateTimeText } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import { useAdminRead } from '../../useAdminRead';

const ON_TEXT =
  'On: trace.io/pricing offers the Trace Enterprise trial, and companies can sign themselves up.';
const OFF_TEXT = 'Off: trace.io/pricing shows the contact form, and new sign-ups are refused.';

const CHOICES = [
  {
    value: 'on',
    label: 'On',
    description:
      'The Enterprise buttons on trace.io/pricing read "Start 14-day trial": a company with a work email signs itself up, adds a card and lands in Trace Enterprise.',
  },
  {
    value: 'off',
    label: 'Off',
    description:
      'The pricing page shows the contact form and new sign-ups are refused. Sign-ups already past the card still finish.',
  },
] as const;
type Choice = (typeof CHOICES)[number]['value'];

/** The change itself: on or off, with the reason recorded in the audit log with the staff member's name. */
function SelfServeDialog({
  settings,
  open,
  onClose,
  onSaved,
}: {
  settings: SelfServeSettings;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { api } = useAdmin();
  const [choice, setChoice] = useState<Choice>(settings.enabled ? 'on' : 'off');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // platform-api answers a request for the state the switch is already in without recording anything, so Save is
  // offered only for the other state.
  const unchanged = (choice === 'on') === settings.enabled;
  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title="Self-serve sign-up"
      description="The pricing page follows within about a minute: the site keeps the answer for 30 seconds."
      submitLabel="Save"
      submitDisabled={unchanged}
      onSubmit={async () => {
        const found = reasonError(reason);
        setError(found);
        if (found) return 'invalid';
        await api.setSelfServe(choice === 'on', reason);
        return undefined;
      }}
      onSaved={() => onSaved(choice === 'on' ? 'Self-serve sign-up is on.' : 'Self-serve sign-up is off.')}
    >
      <RadioGroup
        legend="Self-serve sign-up"
        value={choice}
        onChange={(value) => setChoice(value as Choice)}
        choices={CHOICES}
      />
      <ReasonField value={reason} onChange={setReason} error={error} />
    </WriteDialog>
  );
}

/**
 * The self-serve switch (spec 2026-10-18 decision 5, piece 8): whether companies can start a Trace Enterprise trial
 * on their own from trace.io/pricing. Staff who read billing see it; staff who manage billing change it. A failed read
 * shows the failure and no change button, never a guessed "Off": staff decide to turn it on from this line.
 */
export function SelfServeSection() {
  const abilities = useAbilities();
  const { api } = useAdmin();
  const read = useAdminRead('self-serve-settings', (signal) => api.selfServeSettings(signal));
  const dialog = useDialogTarget<'switch'>();
  const [notice, setNotice] = useState<string | null>(null);
  const settings = read.failure ? null : read.data;

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the self-serve switch" onRetry={read.retry} />;
  } else if (!settings) {
    body = <LoadingBlock label="Loading the self-serve switch" />;
  } else {
    body = (
      <>
        <p>{settings.enabled ? ON_TEXT : OFF_TEXT}</p>
        {settings.updatedBy && settings.updatedAt ? (
          <p className="admin-muted">
            Last changed by {settings.updatedBy.displayName}, <DateTimeText iso={settings.updatedAt} />.
          </p>
        ) : (
          <p className="admin-muted">Not changed in Trace Admin yet.</p>
        )}
      </>
    );
  }

  return (
    <PageSection
      title="Self-serve sign-up"
      description="Whether companies can start a Trace Enterprise trial on their own from trace.io/pricing. Brokers, Carriers and Agents keep the contact form either way."
      action={
        abilities.manageBilling && settings ? (
          <button type="button" className="admin-action" onClick={() => dialog.show('switch')}>
            Change self-serve sign-up
          </button>
        ) : undefined
      }
    >
      <SavedNotice message={notice} />
      {body}
      {abilities.manageBilling && settings && dialog.target ? (
        <SelfServeDialog
          key={dialog.key}
          settings={settings}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={(message) => {
            setNotice(message);
            read.retry();
          }}
        />
      ) : null}
    </PageSection>
  );
}
